// Pure viewBox math for the map. No React, no DOM — everything the pan/zoom
// controller does is a function from one MapView to another, which is why the
// awkward parts (clamping at the edges, zooming toward the cursor, fitting a state's
// bounds) are unit-tested instead of debugged by dragging a map around.
//
// The coordinate space is the projection space shared by every geometry module:
// 960x600, produced by geoAlbersUsa().fitSize([960, 600]) in scripts/geo-shared.mjs.

export const MAP_WIDTH = 960;
export const MAP_HEIGHT = 600;
export const MAP_ASPECT = MAP_WIDTH / MAP_HEIGHT;

export const MIN_SCALE = 1;
/** Roughly "one small county fills the frame" — past this the geometry has no more detail. */
export const MAX_SCALE = 32;

/** [x0, y0, x1, y1] in map units. */
export type Bounds = readonly [number, number, number, number];

export const US_BOUNDS: Bounds = [0, 0, MAP_WIDTH, MAP_HEIGHT];

/**
 * A viewport: where the camera is centred and how far in it is zoomed. Stored this
 * way rather than as a raw viewBox rect so the aspect ratio can never drift and a
 * zoom is a single number to animate, clamp, and put in the URL.
 */
export interface MapView {
  cx: number;
  cy: number;
  scale: number;
}

export const DEFAULT_VIEW: MapView = { cx: MAP_WIDTH / 2, cy: MAP_HEIGHT / 2, scale: 1 };

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function clampNumber(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Round to 3 decimals — enough for smooth motion, short enough for a URL. */
function tidy(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/**
 * Keep the viewport inside the map. At scale 1 this pins the view to the full extent,
 * so you can never drag the country off screen and be left staring at nothing.
 */
export function clampView(view: MapView): MapView {
  const scale = clampNumber(Number.isFinite(view.scale) ? view.scale : 1, MIN_SCALE, MAX_SCALE);
  const w = MAP_WIDTH / scale;
  const h = MAP_HEIGHT / scale;
  const cx = Number.isFinite(view.cx) ? view.cx : DEFAULT_VIEW.cx;
  const cy = Number.isFinite(view.cy) ? view.cy : DEFAULT_VIEW.cy;
  return {
    cx: tidy(clampNumber(cx, w / 2, MAP_WIDTH - w / 2)),
    cy: tidy(clampNumber(cy, h / 2, MAP_HEIGHT - h / 2)),
    scale: tidy(scale),
  };
}

export function rectOf(view: MapView): Rect {
  const v = clampView(view);
  const w = MAP_WIDTH / v.scale;
  const h = MAP_HEIGHT / v.scale;
  return { x: tidy(v.cx - w / 2), y: tidy(v.cy - h / 2), w: tidy(w), h: tidy(h) };
}

export function viewBoxOf(view: MapView): string {
  const { x, y, w, h } = rectOf(view);
  return `${x} ${y} ${w} ${h}`;
}

export function visibleBounds(view: MapView): Bounds {
  const { x, y, w, h } = rectOf(view);
  return [x, y, x + w, y + h];
}

export function boundsOverlap(a: Bounds, b: Bounds): boolean {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

export function boundsCentre(b: Bounds): { x: number; y: number } {
  return { x: (b[0] + b[2]) / 2, y: (b[1] + b[3]) / 2 };
}

/**
 * Frame a feature. `padding` is the fraction of the frame left as margin, so a state
 * never touches the edges of the viewport (or the detail panel that overlaps it).
 */
export function fitBounds(bounds: Bounds, padding = 0.14): MapView {
  const [x0, y0, x1, y1] = bounds;
  const w = Math.max(x1 - x0, 1e-6);
  const h = Math.max(y1 - y0, 1e-6);
  const room = Math.max(0, 1 - padding * 2);
  const scale = Math.min(MAP_WIDTH / (w / room), MAP_HEIGHT / (h / room));
  return clampView({ cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, scale });
}

/**
 * Zoom by a multiplier. With `focus` (a point in map units, e.g. under the cursor or
 * between two fingers) that point stays put, which is what makes wheel and pinch zoom
 * feel like the map rather than the window is moving.
 */
export function zoomBy(view: MapView, factor: number, focus?: { x: number; y: number }): MapView {
  const from = clampView(view);
  const scale = clampNumber(from.scale * factor, MIN_SCALE, MAX_SCALE);
  if (!focus || scale === from.scale) return clampView({ ...from, scale });
  // Keep `focus` at the same fractional offset from the centre after the zoom.
  const k = from.scale / scale;
  return clampView({
    cx: focus.x + (from.cx - focus.x) * k,
    cy: focus.y + (from.cy - focus.y) * k,
    scale,
  });
}

/** Pan by a delta in *map* units. */
export function panBy(view: MapView, dx: number, dy: number): MapView {
  const from = clampView(view);
  return clampView({ ...from, cx: from.cx + dx, cy: from.cy + dy });
}

/**
 * Convert a distance in rendered CSS pixels to map units. The SVG uses
 * preserveAspectRatio="xMidYMid meet", so the scale factor is whichever axis is the
 * constraint — that is the same factor for both axes, which is what keeps a drag
 * tracking the pointer exactly.
 */
export function pixelsToMapUnits(
  view: MapView,
  pixels: number,
  element: { width: number; height: number },
): number {
  const { w, h } = rectOf(view);
  if (element.width <= 0 || element.height <= 0) return 0;
  const renderedScale = Math.min(element.width / w, element.height / h);
  return renderedScale > 0 ? pixels / renderedScale : 0;
}

/**
 * Where a client point lands in map units. Mirrors the letterboxing that
 * preserveAspectRatio="xMidYMid meet" applies, so a click on the coastline maps to
 * the coastline and not to a point offset by the letterbox bars.
 */
export function clientPointToMap(
  view: MapView,
  point: { x: number; y: number },
  rect: { left: number; top: number; width: number; height: number },
): { x: number; y: number } {
  const { x, y, w, h } = rectOf(view);
  if (rect.width <= 0 || rect.height <= 0) return { x: view.cx, y: view.cy };
  const renderedScale = Math.min(rect.width / w, rect.height / h);
  const drawnW = w * renderedScale;
  const drawnH = h * renderedScale;
  const offsetX = (rect.width - drawnW) / 2;
  const offsetY = (rect.height - drawnH) / 2;
  return {
    x: x + (point.x - rect.left - offsetX) / renderedScale,
    y: y + (point.y - rect.top - offsetY) / renderedScale,
  };
}

export function viewsEqual(a: MapView, b: MapView): boolean {
  return a.cx === b.cx && a.cy === b.cy && a.scale === b.scale;
}

/** Interpolate between two views. Zoom is interpolated in log space so a 1x -> 16x
 *  flight feels like constant speed rather than a lurch at the start. */
export function lerpView(from: MapView, to: MapView, t: number): MapView {
  const k = clampNumber(t, 0, 1);
  const scale = Math.exp(Math.log(from.scale) + (Math.log(to.scale) - Math.log(from.scale)) * k);
  return clampView({
    cx: from.cx + (to.cx - from.cx) * k,
    cy: from.cy + (to.cy - from.cy) * k,
    scale,
  });
}

export function easeInOutCubic(t: number): number {
  const k = clampNumber(t, 0, 1);
  return k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
}

/**
 * Stroke width that stays visually constant as you zoom in: the SVG scales strokes
 * with the viewBox, so the authored width has to shrink by the same factor.
 */
export function strokeFor(view: MapView, base: number): number {
  return tidy(base / clampView(view).scale);
}

/**
 * Bounding box of an SVG path emitted by scripts/geo-shared.mjs. Those paths are
 * always absolute "M x,y L x,y ... Z" with no curves, so scanning the coordinate
 * pairs is exact — and far cheaper than asking the DOM for getBBox(), which forces
 * layout and does not work during SSR.
 */
export function boundsOfPath(d: string): Bounds {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const m of d.matchAll(/(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/g)) {
    const x = +m[1];
    const y = +m[2];
    if (x < x0) x0 = x;
    if (y < y0) y0 = y;
    if (x > x1) x1 = x;
    if (y > y1) y1 = y;
  }
  if (!Number.isFinite(x0)) return US_BOUNDS;
  return [x0, y0, x1, y1];
}

/** Union of several bounding boxes; US_BOUNDS when the list is empty. */
export function unionBounds(list: readonly Bounds[]): Bounds {
  if (!list.length) return US_BOUNDS;
  let [x0, y0, x1, y1] = list[0];
  for (const b of list) {
    if (b[0] < x0) x0 = b[0];
    if (b[1] < y0) y0 = b[1];
    if (b[2] > x1) x1 = b[2];
    if (b[3] > y1) y1 = b[3];
  }
  return [x0, y0, x1, y1];
}
